import {AutocompleteControl, BaseControl, FilterControl, Helper, SelectControl} from 'appShared/classes';
import {ReportTableColumn, ReportTableConfigInterface, ReportTableData} from 'appShared/components/report-table';
import {
    ConstraintsCollection,
    MessageFilterModel,
    MessageRecipientInterface,
    MessagingDistributionChannelsEnum,
    MessagingStatusesEnum,
    RecipientAndAccountInterface,
    UserRelationTypesEnum
} from '@spa/api';
import {DialogButtonTypesEnum, DialogConfigInterface, Pagination} from 'appShared/components';
import {ComponentTypesEnum} from 'appShared/enums';
import {ROW_ID_SEPARATOR} from 'appAssets/selectors'; 

export const MessageRequestRecipientNameFilterKeyCode = 'recipients.fullName';
export const MessageRequestRecipientEmailFilterKeyCode = 'recipients.email';
export const MULTIPLE_RECIPIENT_EXIST_LBL = 'multiple';

export const MessageRequestMessageSelector = 'MessageRequestMessage';
export const MessageRequestRecipientNameSelector = 'MessageRequestRecipientName';
export const MessageRequestSubjectSelector = 'MessageRequestSubject';
export const MessageRequestAuthorSelector = 'MessageRequestAuthor';
export const MessageRequestApprovedBySelector = 'MessageRequestApprovedBy';
export const MessageRequestMessageStatusSelector = 'MessageRequestMessageStatus';
export const MessageRequestEmailAddressSelector = 'MessageRequestEmailAddress';
export const MessageRequestCreatedDateSelector = 'MessageRequestCreatedDate';
export const MessageRequestCreatedFromSelector = 'MessageRequestCreatedFrom';
export const MessageRequestCreatedToSelector = 'MessageRequestCreatedTo';
export const MessageRequestKeyCodeSelector = 'MessageRequestKeyCode';
export const MessageRequestTemplateNameSelector = 'MessageRequestTemplateName';
export const MessageRequestCustomerIdSelector = 'MessageRequestCustomerId';
export const MessageRequestCustomerTypeSelector = 'MessageRequestCustomerType';
export const MessageRequestDistributionChannelsSelector = 'MessageRequestDistributionChannels';
export const MessageRequestImportanceLevelSelector = 'MessageRequestImportanceLevel';

export const MessageRequestRecipientName = 'Recipient Name';
export const MessageRequestEmailAddress = 'Email Address';
export const MessageRequestApprovedBy = 'Approved By';
export const MessageRequestMessageStatus = 'Message Status';
export const MessageRequestSubject = 'Message Subject';
export const MessageRequestKeyCode = 'Key Code';
export const MessageRequestTemplateName = 'Template';
export const MessageRequestCustomer = 'Company';
export const MessageRequestCustomerId = 'Customer Id';
export const MessageRequestCustomerType = 'User Type';
export const MessageRequestCustomerProductId = 'Product Id';
export const MessageRequestDistributionChannels = 'Channels';
export const MessageRequestImportanceLevel = 'Important';

function prepareDefaultTextFilterByIdAndPlaceholder(selector: string, placeholder: string): FilterControl {
    return new FilterControl({
        name: selector,
        placeholder,
        controls: [
            new BaseControl({
                name: selector,
                value: null,
                placeholder,
                validators: [],
                componentType: ComponentTypesEnum.Text
            })
        ],
        sortIndex: 0,
        shown: false,
        isRemovable: true
    });
}

export const RecipientDataFilterControls = (): FilterControl[] => {
    return [new FilterControl({
        name: MessageRequestCustomerIdSelector,
        placeholder: MessageRequestCustomerId,
        controls: [
            new AutocompleteControl({
                name: MessageRequestCustomerIdSelector,
                value: null,
                placeholder: MessageRequestCustomerId,
                validators: [],
                componentType: ComponentTypesEnum.Autocomplete,
                options: []
            })
        ],
        sortIndex: 1,
        shown: true,
        isRemovable: false
    }), new FilterControl({
        name: MessageRequestCustomerTypeSelector,
        placeholder: MessageRequestCustomerType,
        controls: [
            new SelectControl({
                name: MessageRequestCustomerTypeSelector,
                value: null,
                placeholder: MessageRequestCustomerType,
                validators: [],
                componentType: ComponentTypesEnum.Select,
                options: [
                    {
                        label: 'All',
                        value: null
                    },
                    {
                        label: Helper.textsHelper().getUserRelationType(UserRelationTypesEnum.Individual),
                        value: UserRelationTypesEnum.Individual
                    },
                    {
                        label: Helper.textsHelper().getUserRelationType(UserRelationTypesEnum.CompanyEmployee),
                        value: UserRelationTypesEnum.CompanyEmployee
                    },
                    {
                        label: Helper.textsHelper().getUserRelationType(UserRelationTypesEnum.Operator),
                        value: UserRelationTypesEnum.Operator
                    }
                ]
            })
        ],
        sortIndex: 1,
        shown: true,
        isRemovable: false
    }), new FilterControl({
        name: MessageRequestRecipientNameSelector,
        placeholder: MessageRequestRecipientName,
        controls: [
            new BaseControl({
                name: MessageRequestRecipientNameSelector,
                value: null,
                placeholder: MessageRequestRecipientName,
                validators: [],
                componentType: ComponentTypesEnum.Text,
            })
        ],
        sortIndex: 1,
        shown: true,
        isRemovable: false
    }), new FilterControl({
        name: MessageRequestEmailAddressSelector,
        placeholder: MessageRequestEmailAddress,
        controls: [
            new BaseControl({
                name: MessageRequestEmailAddressSelector,
                value: null,
                placeholder: MessageRequestEmailAddress,
                validators: [],
                componentType: ComponentTypesEnum.Text,
            })
        ],
        sortIndex: 1,
        shown: true,
        isRemovable: false
    })
    ];
};

export const MessageRequestFilterControls = (): FilterControl[] => {
    return [
        prepareDefaultTextFilterByIdAndPlaceholder(MessageRequestRecipientNameSelector, MessageRequestRecipientName),
        prepareDefaultTextFilterByIdAndPlaceholder(MessageRequestEmailAddressSelector, MessageRequestEmailAddress),
        new FilterControl({
            name: MessageRequestCreatedDateSelector,
            placeholder: 'Created Date',
            controls: [
                new BaseControl({
                    name: MessageRequestCreatedFromSelector,
                    value: null,
                    placeholder: 'Created From',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 1
                }),
                new BaseControl({
                    name: MessageRequestCreatedToSelector,
                    value: null,
                    placeholder: 'Created To',
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
            ],
            sortIndex: 0,
            shown: false,
            isRemovable: true
        }),
        prepareDefaultTextFilterByIdAndPlaceholder(MessageRequestSubjectSelector, MessageRequestSubject),
        new FilterControl({
            name: MessageRequestMessageStatusSelector,
            placeholder: MessageRequestMessageStatus,
            controls: [
                new AutocompleteControl({
                    name: MessageRequestMessageStatusSelector,
                    value: null,
                    placeholder: MessageRequestMessageStatus,
                    validators: [],
                    componentType: ComponentTypesEnum.Autocomplete,
                    options: [
                        {
                            label: Helper.textsHelper().getMessagingStatusName(MessagingStatusesEnum.PendingApproval),
                            value: MessagingStatusesEnum.PendingApproval
                        },
                        {
                            label: Helper.textsHelper().getMessagingStatusName(MessagingStatusesEnum.Rejected),
                            value: MessagingStatusesEnum.Rejected
                        },
                        {
                            label: Helper.textsHelper().getMessagingStatusName(MessagingStatusesEnum.Approved),
                            value: MessagingStatusesEnum.Approved
                        },
                    ],
                })
            ],
            sortIndex: 0,
            shown: false,
            isRemovable: true
        }),
        prepareDefaultTextFilterByIdAndPlaceholder(MessageRequestAuthorSelector, 'Created By'),
        prepareDefaultTextFilterByIdAndPlaceholder(MessageRequestApprovedBySelector, MessageRequestApprovedBy),
        new FilterControl({
            name: MessageRequestDistributionChannelsSelector,
            placeholder: MessageRequestDistributionChannels,
            controls: [
                new SelectControl({
                    name: MessageRequestDistributionChannelsSelector,
                    value: null,
                    placeholder: MessageRequestDistributionChannels,
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: Object.values(MessagingDistributionChannelsEnum).map((key: string) => {
                        return {
                            label: Helper.textsHelper().getMessagingDisrtibutionChannelName(key),
                            value: key
                        };
                    }),
                    multiple: true
                })
            ],
            sortIndex: 0,
            shown: false,
            isRemovable: true
        }),
        new FilterControl({
            name: MessageRequestImportanceLevelSelector,
            placeholder: MessageRequestImportanceLevel,
            controls: [
                new SelectControl({
                    name: MessageRequestImportanceLevelSelector,
                    value: null,
                    placeholder: MessageRequestImportanceLevel,
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {
                            label:Helper.textsHelper().getMessagingImportanceLevel(0),
                            value:0
                        },
                        {
                            label:Helper.textsHelper().getMessagingImportanceLevel(1),
                            value:1
                        }
                    ], 
                    multiple: false
                })
            ],
            sortIndex: 0,
            shown: false,
            isRemovable: true
        }),
    ];
};

export const MessageRequestDataTableItems = (): ReportTableData[] => {
    return [
        new ReportTableData<number>({
            colId: MessageRequestMessageSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestTemplateNameSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestRecipientNameSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestSubjectSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestAuthorSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestApprovedBySelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestMessageStatusSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestEmailAddressSelector,
            value: null,
            hidden: false
        }),
        new ReportTableData<number>({
            colId: MessageRequestDistributionChannelsSelector,
            value: null,
            hidden: false
        }), 
        new ReportTableData<number>({
            colId: MessageRequestImportanceLevelSelector,
            value: null,
            hidden: false
        }),
    ];
};

export const MessageRequestDataTableConfig = (): ReportTableConfigInterface => {
    const columns = [
        new ReportTableColumn({
            label: 'Message',
            selector: MessageRequestMessageSelector
        }),
        new ReportTableColumn({
            label: MessageRequestTemplateName,
            selector: MessageRequestTemplateNameSelector
        }),
        new ReportTableColumn({
            label: MessageRequestRecipientName,
            selector: MessageRequestRecipientNameSelector
        }),
        new ReportTableColumn({
            label: MessageRequestSubject,
            selector: MessageRequestSubjectSelector
        }),
        new ReportTableColumn({
            label: 'Author',
            selector: MessageRequestAuthorSelector
        }),
        new ReportTableColumn({
            label: MessageRequestApprovedBy,
            selector: MessageRequestApprovedBySelector
        }),
        new ReportTableColumn({
            label: MessageRequestMessageStatus,
            selector: MessageRequestMessageStatusSelector
        }),
        new ReportTableColumn({
            label: MessageRequestEmailAddress,
            selector: MessageRequestEmailAddressSelector,
        }),
        new ReportTableColumn({
            label: MessageRequestDistributionChannels,
            selector: MessageRequestDistributionChannelsSelector,
        }), 
        new ReportTableColumn({
            label: MessageRequestImportanceLevel,
            selector: MessageRequestImportanceLevelSelector,
        }),
    ];

    return {
        rows: [],
        columns,
        hiddenColumnIds: [],
        actionMenu: false,
        disableColumnsOptions: false
    };
};

export const defaultMessageRequestInitialFilterModel = (itemsPerPage = Pagination.defaultItemsPerPage) => {
    return new MessageFilterModel(
        new ConstraintsCollection(),
        Pagination.defaultPagesChunk - 1,
        MessageFilterModel.getCreatedTimeSelector(),
        Pagination.calculateItemsPerPagesChunk(itemsPerPage),
        false
    );
};

export const RecipientListDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Confirm,
                customText: 'Save List'
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false,
        disabledCloseBtn: true
    };
};

export const getRecipientRowIdByRecipient = (item: RecipientAndAccountInterface) => {
    return `${item.subject_id}${ROW_ID_SEPARATOR}${item.user_relation_type}${ROW_ID_SEPARATOR}${item.email}`;
};

export const getRecipientRowIdByCustomer = (item: MessageRecipientInterface) => {
    return `${item.customerId}${ROW_ID_SEPARATOR}${item.customerType}${ROW_ID_SEPARATOR}${item.email}`;
};
